import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  addCartLines,
  CART_STATUS_LABELS,
  cartQueryKey,
  createCart,
  deleteCart,
  distributeCartToBest,
  fetchCart,
  fetchCarts,
  confirmCart,
  addSpecialCartLine,
  removeCartLine,
  setCartStatus,
  updateCart,
  updateCartLine,
  BEST_PRICE_MONTHS,
  bestPriceWindowLabel,
  type BestPriceMonths,
  type Cart,
  type CartHeader,
  type CartLine,
} from "../api/carts";
import { fetchCostCenters } from "../api/costCenters";
import { fetchPriceReportOptions } from "../api/priceReport";
import { fetchDeliveryAddresses } from "../api/deliveryAddresses";
import { resolveArticleCodes, type ArticleSuggestion } from "../api/articles";
import { useSession } from "../auth/useSession";
import { useActiveCartId } from "../lib/activeCart";
import ArticlePickerSearch from "../components/ArticlePickerSearch";
import QtyStepper from "../components/QtyStepper";
import Select from "../components/Select";
import { ApiError } from "../lib/api";
import { costCenterTypeLabel } from "../lib/costCenterDisplay";
import { cleanDescription } from "../lib/description";
import { formatDateShort, formatEuro, formatQty, parseAmount, toNumber } from "../lib/format";
import { cartCsv, cartCsvFilename, cartEnquiry, mailtoHref } from "../lib/cartExport";
import { downloadOfferCsv } from "../lib/offerCsv";
import { parsePastedCartLines } from "../lib/priceRequest";
import LegacyPriceRequestImport from "./cart/LegacyPriceRequestImport";

type Tab = "current" | "all";
const BUTTON = "h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-default disabled:opacity-50";
const PRIMARY = "h-9 rounded-md bg-emerald-700 px-3 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-default disabled:opacity-50";
const displayUnit = (unit: string | null) => unit?.trim().toUpperCase() === "ST" ? "Stk" : unit ?? "";

export default function CartPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const tab: Tab = requested === "all" ? "all" : "current";
  const [, setActiveCartId] = useActiveCartId();
  // Choosing a cart makes it the active one for the whole app.
  const selectCart = (cartId: string) => {
    setActiveCartId(cartId || null);
    setParams(cartId ? { cart: cartId } : {});
  };
  return (
    <main className="mx-auto max-w-7xl px-6 py-6">
      <h1 className="text-2xl font-semibold text-slate-900">Warenkorb</h1>
      <nav aria-label="Warenkorb" className="mt-4 flex gap-6 border-b border-slate-200 text-sm" role="tablist">
        {([["current", "Aktuell"], ["all", "Alle Warenkörbe"]] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setParams(value === "current" ? {} : { tab: value })}
            className={`-mb-px border-b-2 py-2 font-medium ${tab === value ? "border-emerald-700 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === "all" ? <AllCarts onOpen={selectCart} /> : null}
      {tab === "current" ? <CurrentCart cartId={params.get("cart")} onSelect={selectCart} /> : null}
    </main>
  );
}

function CurrentCart({ cartId, onSelect }: { cartId: string | null; onSelect: (cartId: string) => void }) {
  const queryClient = useQueryClient();
  const mine = useQuery({ queryKey: ["carts", "list", "mine", "open"], queryFn: () => fetchCarts("mine", null, "open") });
  const { user } = useSession();
  const [activeCartId, setActiveCartId] = useActiveCartId();
  const openIds = mine.data?.items.map((cart) => cart.cart_id) ?? [];
  const selectedId = cartId ?? (activeCartId && openIds.includes(activeCartId) ? activeCartId : null) ?? mine.data?.items[0]?.cart_id ?? null;
  // A link to a cart (?cart=…) opens and activates it, like choosing it here.
  const linkedOpenCart = cartId && openIds.includes(cartId) ? cartId : null;
  useEffect(() => {
    if (linkedOpenCart) setActiveCartId(linkedOpenCart);
  }, [linkedOpenCart, setActiveCartId]);
  const create = useMutation({
    mutationFn: () => createCart(),
    onSuccess: (cart) => {
      queryClient.setQueryData(cartQueryKey(cart.cart_id), cart);
      void queryClient.invalidateQueries({ queryKey: ["carts", "list"] });
      onSelect(cart.cart_id);
    },
  });

  if (mine.isLoading) return <p className="mt-6 text-sm text-slate-500">Lade Warenkörbe …</p>;
  if (mine.isError) return <ErrorText error={mine.error} fallback="Die Warenkörbe konnten nicht geladen werden." />;
  const openCarts = mine.data?.items ?? [];
  const canCreate = mine.data?.can_create ?? false;
  return (
    <div className="mt-4">
      {canCreate && user ? <LegacyPriceRequestImport key={JSON.stringify([user.customer_id, user.user_id])} identity={user} onSelect={onSelect} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        {openCarts.map((cart) => (
          <CartPill
            key={cart.cart_id}
            cart={cart}
            selected={cart.cart_id === selectedId}
            canDelete={canCreate}
            onSelect={() => onSelect(cart.cart_id)}
            onDeleted={() => onSelect("")}
          />
        ))}
        {canCreate ? (
          <button type="button" className={BUTTON} onClick={() => create.mutate()} disabled={create.isPending}>
            + Neuer Warenkorb
          </button>
        ) : null}
      </div>
      {selectedId ? (
        <CartDetail cartId={selectedId} />
      ) : (
        <p className="mt-6 rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-600">
          Noch kein offener Warenkorb. Lege Artikel über „In den Warenkorb“ in der Artikelliste ab oder starte einen neuen Warenkorb.
        </p>
      )}
    </div>
  );
}

/** One open cart with a direct way to close it (×). */
function CartPill({
  cart,
  selected,
  canDelete,
  onSelect,
  onDeleted,
}: {
  cart: CartHeader;
  selected: boolean;
  canDelete: boolean;
  onSelect: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState<CartHeader | null>(null);
  const remove = useMutation({
    // Delete only the revision shown to the user, never silently adopt newer contents.
    mutationFn: (confirmed: CartHeader) => deleteCart(confirmed),
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        setAsking(null);
        void queryClient.invalidateQueries({ queryKey: ["carts", "list"] });
        void queryClient.invalidateQueries({ queryKey: cartQueryKey(cart.cart_id) });
      }
    },
    onSuccess: () => {
      setAsking(null);
      queryClient.removeQueries({ queryKey: cartQueryKey(cart.cart_id) });
      void queryClient.invalidateQueries({ queryKey: ["carts", "list"] });
      if (selected) onDeleted();
    },
  });
  const pill = `inline-flex h-8 items-center rounded-full border pl-3 text-sm ${canDelete ? "pr-1" : "pr-3"} ${
    selected ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-900" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
  }`;
  if (asking) {
    // An empty cart goes without asking; one with positions asks here, in the chip.
    return (
      <span role="group" aria-label={`Warenkorb ${cart.name} löschen?`} className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl border border-rose-200 bg-rose-50 py-1 pl-3 pr-1 text-sm text-rose-900">
        <span className="min-w-0">„{asking.name}“ mit {asking.line_count} {asking.line_count === 1 ? "Position" : "Positionen"} löschen?</span>
        <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(asking)} className="rounded-full bg-rose-700 px-2.5 py-0.5 text-xs font-medium text-white hover:bg-rose-800 disabled:opacity-50">
          {remove.isPending ? "Lösche …" : "Löschen"}
        </button>
        <button type="button" disabled={remove.isPending} onClick={() => { setAsking(null); remove.reset(); }} className="rounded-full px-2 py-0.5 text-xs text-rose-900 hover:bg-white">
          Abbrechen
        </button>
        {remove.isError ? <span role="alert" className="pr-2 text-xs text-rose-700">{remove.error instanceof ApiError ? remove.error.message : "Konnte nicht gelöscht werden."}</span> : null}
      </span>
    );
  }
  return (
    <span className={pill}>
      <button type="button" onClick={onSelect} aria-pressed={selected} className="whitespace-nowrap">
        {cart.name} <span className="text-slate-500">· {cart.line_count}</span>
      </button>
      {canDelete ? (
        <button
          type="button"
          aria-label={`Warenkorb ${cart.name} löschen`}
          disabled={remove.isPending}
          onClick={() => (cart.line_count === 0 ? remove.mutate(cart) : setAsking(cart))}
          className="ml-1 rounded-full px-1.5 text-slate-400 hover:bg-white hover:text-rose-700"
          title="Warenkorb löschen"
        >
          ×
        </button>
      ) : null}
      {remove.isError ? <span role="alert" className="ml-2 text-xs text-rose-700">{remove.error instanceof ApiError ? remove.error.message : "Konnte nicht gelöscht werden."}</span> : null}
    </span>
  );
}

function CartDetail({ cartId }: { cartId: string }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: cartQueryKey(cartId), queryFn: () => fetchCart(cartId) });
  const [conflict, setConflict] = useState<string | null>(null);
  // Edits run one after another, each on the latest revision, so fields stay usable while saving.
  const queue = useRef(Promise.resolve());
  const change = useMutation({
    mutationFn: (run: (cart: Cart) => Promise<Cart>) => run(queryClient.getQueryData<Cart>(cartQueryKey(cartId))!),
    onSuccess: (cart) => {
      setConflict(null);
      queryClient.setQueryData(cartQueryKey(cart.cart_id), cart);
      void queryClient.invalidateQueries({ queryKey: ["carts", "list"] });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        setConflict(error.message);
        void query.refetch();
      }
    },
  });

  if (query.isLoading) return <p className="mt-6 text-sm text-slate-500">Lade Warenkorb …</p>;
  if (query.isError || !query.data) return <ErrorText error={query.error} fallback="Der Warenkorb konnte nicht geladen werden." />;
  const cart = query.data;
  const edit = cart.can_edit;
  const run = (fn: (cart: Cart) => Promise<Cart>) => {
    queue.current = queue.current.then(() => change.mutateAsync(fn)).then(() => undefined, () => undefined);
  };

  return (
    <section className="mt-4 space-y-4" aria-label={cart.name}>
      <CartHead cart={cart} edit={edit} run={run} />
      {cart.status_marked ? (
        <p className="text-sm text-slate-600">
          Angefragt von {cart.status_marked.by} am {formatDateShort(cart.status_marked.at)} · Ändern bleibt möglich, bis die Bestellung bestätigt ist.
        </p>
      ) : null}
      {conflict ? <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">{conflict}</p> : null}
      {change.isError && !(change.error instanceof ApiError && change.error.status === 409) ? (
        <ErrorText error={change.error} fallback="Die Änderung konnte nicht gespeichert werden." />
      ) : null}
      {cart.can_edit ? <AddArticles run={run} disabled={!edit} /> : null}
      {cart.lines.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-600">Dieser Warenkorb ist leer.</p>
      ) : (
        cart.groups.map((group) => (
          <SupplierGroup
            key={group.supplier ?? "none"}
            cart={cart}
            supplier={group.supplier}
            total={group.total}
            complete={group.complete}
            lines={cart.lines.filter((line) => group.line_ids.includes(line.line_id))}
            edit={edit}
            run={run}
          />
        ))
      )}
      <footer className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <p className="text-sm text-slate-700">
            Summe <span className="text-base font-semibold text-slate-900">{formatEuro(cart.total)}</span>
            {cart.total_complete ? null : <span className="text-slate-500"> · ohne Positionen ohne Preis</span>}
            {toNumber(cart.saving_at_best) ? (
              <span> · zum Bestpreis <span className="font-medium text-emerald-800">−{formatEuro(cart.saving_at_best)}</span></span>
            ) : null}
          </p>
          {cart.can_edit ? <DeleteCart cart={cart} /> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={BUTTON}
            disabled={cart.lines.length === 0}
            onClick={() => downloadOfferCsv(cartCsv(cart), cartCsvFilename(cart))}
          >
            Export für Excel
          </button>
          {cart.can_edit ? (
            <>
              {/* Nur anbieten, wenn es wirklich etwas zu verteilen gibt (#630). */}
              {cart.lines.some((line) => line.cheaper_elsewhere) ? (
                <button type="button" className={BUTTON} disabled={!edit} onClick={() => run(distributeCartToBest)}>
                  Alle zum Bestpreis verteilen
                </button>
              ) : null}
              <button
                type="button"
                className={BUTTON}
                disabled={!edit || cart.lines.length === 0}
                onClick={() => run((c) => setCartStatus(c, cart.status === "requested" ? "open" : "requested"))}
              >
                {cart.status === "requested" ? "Doch nicht angefragt" : "Als angefragt markieren"}
              </button>
              <ConfirmOrder cart={cart} run={run} disabled={!edit} />
            </>
          ) : null}
        </div>
      </footer>
    </section>
  );
}

function CartHead({ cart, edit, run }: { cart: Cart; edit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void }) {
  const [name, setName] = useState(cart.name);
  const [renaming, setRenaming] = useState(false);
  useEffect(() => setName(cart.name), [cart.name]);
  const saveName = () => {
    setRenaming(false);
    if (name.trim() && name !== cart.name) run((current) => updateCart(current, { name }));
    else setName(cart.name);
  };
  const rule = `${cart.rule.basis === "best" ? "Bester" : "Letzter"} Einkauf · 12 Monate · ${
    toNumber(cart.rule.discount_pct) ? `${formatQty(cart.rule.discount_pct)} % Abschlag` : "ohne Abschlag"
  }`;
  return (
    <div className={`grid min-w-0 gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-2 ${cart.rule.revision ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
      {/* Name als Text; erst der Stift öffnet das Eingabefeld. */}
      <div className="min-w-0 text-sm">
        <span className="block text-xs font-medium text-slate-600">Name</span>
        {cart.can_edit && renaming ? (
          <input
            value={name}
            maxLength={100}
            disabled={!edit}
            autoFocus
            aria-label="Name des Warenkorbs"
            onChange={(event) => setName(event.target.value)}
            onBlur={saveName}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveName();
              if (event.key === "Escape") { setName(cart.name); setRenaming(false); }
            }}
            className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm"
          />
        ) : (
          <span className="mt-1 flex min-h-9 items-center gap-1.5 text-slate-900">
            <span className="min-w-0 break-words">{cart.name}</span>
            {cart.can_edit ? (
              <button
                type="button"
                disabled={!edit}
                onClick={() => setRenaming(true)}
                aria-label="Warenkorb umbenennen"
                title="Warenkorb umbenennen"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
              >
                <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16.9 3.6a2.1 2.1 0 0 1 3 3L8.5 18 4 19.5 5.5 15Z" /><path d="m14.5 6 3.5 3.5" /></svg>
              </button>
            ) : <span className="text-slate-500">· {cart.owner.name}</span>}
          </span>
        )}
      </div>
      <CostCenterField cart={cart} edit={edit} run={run} />
      <DeliveryAddressField cart={cart} edit={edit} run={run} />
      <BestPriceWindowField cart={cart} edit={edit} run={run} />
      {/* Nur mit echter Unternehmensvorgabe (#630). */}
      {cart.rule.revision ? <div className="min-w-0 text-sm">
        <span className="block text-xs font-medium text-slate-600">Preisvorgabe</span>
        <span className="mt-1 block text-slate-900" title="Vorgabe der Geschäftsführung; im Warenkorb nicht änderbar">{rule}</span>
      </div> : null}
    </div>
  );
}

/**
 * Wie weit der Bestpreis zurückschaut – je Warenkorb wählbar. Referenz und
 * Vorgabe bleiben bei der Unternehmensregel (12 Monate).
 */
function BestPriceWindowField({ cart, edit, run }: { cart: Cart; edit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void }) {
  return (
    <label className="min-w-0 text-sm">
      <span className="block text-xs font-medium text-slate-600">Bestpreis aus den letzten</span>
      {cart.can_edit ? (
        <Select
          containerClassName="mt-1 block"
          value={cart.best_price_months}
          disabled={!edit}
          aria-label="Zeitraum für den Bestpreis"
          title="Niedrigster Rechnungspreis in diesem Zeitraum. Referenz und Vorgabe bleiben bei 12 Monaten."
          onChange={(event) => {
            const months = Number(event.target.value) as BestPriceMonths;
            if (months !== cart.best_price_months) run((current) => updateCart(current, { best_price_months: months }));
          }}
        >
          {BEST_PRICE_MONTHS.map((months) => <option key={months} value={months}>{bestPriceWindowLabel(months, "dative")}</option>)}
        </Select>
      ) : (
        <span className="mt-1 block text-slate-900">{bestPriceWindowLabel(cart.best_price_months, "dative")}</span>
      )}
    </label>
  );
}

function CostCenterField({ cart, edit, run }: { cart: Cart; edit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const results = useQuery({
    queryKey: ["cost-centers", "picker", search.trim()],
    queryFn: () => fetchCostCenters({ q: search.trim(), limit: 8 }),
    enabled: cart.can_edit && open,
  });
  const current = cart.cost_center;
  return (
    <div className="relative min-w-0 text-sm">
      <span className="block text-xs font-medium text-slate-600">Kostenstelle</span>
      {current && !open ? (
        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="truncate text-slate-900" title={current.label}>{current.label}</span>
          <span className="shrink-0 text-xs text-slate-500">{costCenterTypeLabel(current.type)}</span>
          {cart.can_edit ? (
            <button type="button" className="text-xs text-slate-600 underline" disabled={!edit} onClick={() => setOpen(true)}>ändern</button>
          ) : null}
        </span>
      ) : cart.can_edit ? (
        <>
          <input
            value={search}
            disabled={!edit}
            onFocus={() => setOpen(true)}
            onChange={(event) => { setSearch(event.target.value); setOpen(true); }}
            placeholder="Kostenstelle suchen …"
            aria-label="Kostenstelle suchen"
            className={`mt-1 h-9 w-full rounded-md border px-3 text-sm ${current ? "border-slate-300" : "border-amber-400"}`}
          />
          {!current ? <span className="text-xs text-amber-800">Pflicht vor der Bestellung</span> : null}
          {open ? (
            <ul className="absolute z-20 mt-1 max-h-72 w-[28rem] max-w-[90vw] overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
              {results.isLoading ? <li className="px-3 py-2 text-slate-500">Lade Kostenstellen …</li> : null}
              {results.isError ? <li className="px-3 py-2 text-rose-700">Kostenstellen konnten nicht geladen werden.</li> : null}
              {!results.isFetching && !results.isError && !results.data?.items.length ? <li className="px-3 py-2 text-slate-500">Keine Kostenstelle gefunden.</li> : null}
              {!results.isFetching && results.data?.items.map((item) => (
                <li key={`${item.cost_center_type}:${item.cost_center_key}`}>
                  <button
                    type="button"
                    className="flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
                    onClick={() => {
                      setOpen(false);
                      setSearch("");
                      run((c) => updateCart(c, { cost_center: { type: item.cost_center_type as "project", key: item.cost_center_key } }));
                    }}
                  >
                    <span className="truncate">{item.display_label ?? item.cost_center_key}</span>
                    <span className="shrink-0 text-xs text-slate-500">{costCenterTypeLabel(item.cost_center_type)}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        <span className="mt-1 block text-slate-500">nicht gewählt</span>
      )}
    </div>
  );
}

function DeliveryAddressField({ cart, edit, run }: { cart: Cart; edit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const costCenter = cart.cost_center ? { type: cart.cost_center.type, key: cart.cost_center.key } : null;
  const query = useQuery({
    queryKey: ["delivery-addresses", "cart", costCenter?.key ?? null, search.trim()],
    queryFn: () => fetchDeliveryAddresses({ q: search.trim() || undefined, limit: 20, costCenter }),
    enabled: cart.can_edit && open,
  });
  const current = cart.delivery_address;

  if (!cart.can_edit) {
    return (
      <div className="min-w-0 text-sm">
        <span className="block text-xs font-medium text-slate-600">Lieferung</span>
        <span className="mt-1 block text-slate-900">{current?.label ?? "nicht gewählt"}</span>
      </div>
    );
  }
  const choose = (key: string | null) => {
    setOpen(false);
    setSearch("");
    run((c) => updateCart(c, key ? { delivery_address_key: key } : { clear_delivery_address: true }));
  };
  // Addresses used on this cost center come first; the search covers everything else.
  const suggested = (query.data?.suggested ?? []).filter((item) => item.delivery_address_key !== current?.key);
  const rest = (query.data?.items ?? []).filter(
    (item) => item.delivery_address_key !== current?.key && !suggested.some((hit) => hit.delivery_address_key === item.delivery_address_key),
  );

  return (
    <div className="relative min-w-0 text-sm">
      <span className="block text-xs font-medium text-slate-600">Lieferung</span>
      {current && !open ? (
        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="truncate text-slate-900" title={current.label}>{current.label}</span>
          <button type="button" className="text-xs text-slate-600 underline" disabled={!edit} onClick={() => setOpen(true)}>ändern</button>
        </span>
      ) : (
        <>
          <input
            value={search}
            disabled={!edit}
            onFocus={() => setOpen(true)}
            onChange={(event) => { setSearch(event.target.value); setOpen(true); }}
            placeholder="Lieferadresse suchen …"
            aria-label="Lieferadresse suchen"
            className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm"
          />
          {open ? (
            <ul className="absolute z-20 mt-1 max-h-72 w-[28rem] max-w-[90vw] overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
              {suggested.length ? (
                <li className="px-3 pb-1 pt-2 text-xs font-medium text-slate-500">
                  {cart.cost_center ? `Häufig bei ${cart.cost_center.label}` : "Vorschläge"}
                </li>
              ) : null}
              {[...suggested, ...rest].map((item, index) => (
                <li key={item.delivery_address_key}>
                  {index === suggested.length && suggested.length ? (
                    <p className="border-t border-slate-100 px-3 pb-1 pt-2 text-xs font-medium text-slate-500">Alle Lieferadressen</p>
                  ) : null}
                  <button
                    type="button"
                    className="flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
                    onClick={() => choose(item.delivery_address_key)}
                  >
                    <span className="truncate">{item.delivery_address_display ?? item.delivery_address_key}</span>
                    {index < suggested.length ? (
                      <span className="shrink-0 text-xs text-slate-500">{item.line_count === 1 ? "1 Position" : `${item.line_count} Positionen`}</span>
                    ) : null}
                  </button>
                </li>
              ))}
              {current ? (
                <li>
                  <button type="button" className="w-full border-t border-slate-100 px-3 py-2 text-left text-slate-600 hover:bg-slate-50" onClick={() => choose(null)}>
                    Lieferadresse entfernen
                  </button>
                </li>
              ) : null}
              {query.isLoading ? <li className="px-3 py-2 text-slate-500">Lade Lieferadressen …</li> : null}
            </ul>
          ) : null}
        </>
      )}
    </div>
  );
}

function AddArticles({ run, disabled }: { run: (fn: (cart: Cart) => Promise<Cart>) => void; disabled: boolean }) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [specialOpen, setSpecialOpen] = useState(false);
  const [text, setText] = useState("");
  const [unresolved, setUnresolved] = useState<string[]>([]);
  const resolve = useMutation({
    mutationFn: async () => {
      const parsed = parsePastedCartLines(text);
      const response = await resolveArticleCodes(parsed.map((item) => item.code));
      const qty = new Map(parsed.map((item) => [item.code.trim(), item.qty]));
      const matched = response.items.filter((item) => item.status === "matched" && item.article_group_key);
      return {
        items: matched.map((item) => ({ article_group_key: item.article_group_key!, qty: qty.get(item.input) ?? 1 })),
        unresolved: response.items.filter((item) => !matched.includes(item)).map((item) => item.input),
      };
    },
    onSuccess: (result) => {
      setUnresolved(result.unresolved);
      if (result.items.length) run((c) => addCartLines(c, result.items));
      setText("");
    },
  });
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[16rem] flex-1">
          <ArticlePickerSearch
            placeholder="Artikel suchen und in den Warenkorb legen …"
            onSelect={(article: ArticleSuggestion) => !disabled && run((c) => addCartLines(c, [{ article_group_key: article.article_group_key, qty: 1 }]))}
          />
        </div>
        <button type="button" className={BUTTON} onClick={() => setPasteOpen((value) => !value)} aria-expanded={pasteOpen}>
          Artikelnummern einfügen
        </button>
        <button type="button" className={BUTTON} onClick={() => setSpecialOpen((value) => !value)} aria-expanded={specialOpen}>
          Sonderposition hinzufügen
        </button>
      </div>
      {specialOpen ? <SpecialLineForm run={run} disabled={disabled} onDone={() => setSpecialOpen(false)} /> : null}
      {pasteOpen ? (
        <div className="mt-3">
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={4}
            aria-label="Artikelnummern mit Menge, eine Position pro Zeile"
            placeholder={"123456 10\n789012; 4\n345678"}
            className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
          />
          <button type="button" className={`${PRIMARY} mt-2`} disabled={disabled || !text.trim() || resolve.isPending} onClick={() => resolve.mutate()}>
            {resolve.isPending ? "Suche Artikel …" : "Übernehmen"}
          </button>
        </div>
      ) : null}
      {unresolved.length ? (
        <p role="status" className="mt-2 text-sm text-amber-800">
          Nicht eindeutig gefunden: {unresolved.join(", ")}. Bitte über die Suche auswählen.
        </p>
      ) : null}
    </div>
  );
}

/** Menge einer Position als „− 2 +“ wie in der Artikelliste; die Zahl lässt sich auch eintippen. */
function QtyField({ line, edit, run }: { line: CartLine; edit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void }) {
  const qty = toNumber(line.qty) ?? 0;
  const set = (value: number) => run((c) => updateCartLine(c, line.line_id, { qty: value }));
  const step = (delta: number) => run((current) => {
    const currentLine = current.lines.find((item) => item.line_id === line.line_id);
    if (!currentLine) throw new Error("Die Position ist nicht mehr im Warenkorb.");
    return updateCartLine(current, line.line_id, { qty: Math.max(1, (toNumber(currentLine.qty) ?? 0) + delta) });
  });
  return (
    <span className="inline-flex items-center gap-1.5">
      <QtyStepper
        value={line.qty}
        label={`Menge ${line.article_code ?? cleanDescription(line.description) ?? ""}`.trim()}
        size="sm"
        disabled={!edit}
        // Removing stays the × of the line; "−" stops at the smallest step.
        lessDisabled={qty <= 1}
        onLess={() => step(-1)}
        onMore={() => step(1)}
        onSet={set}
      />
      <span className="text-xs text-slate-500">{displayUnit(line.uom)}</span>
    </span>
  );
}

function ReferenceCell({ line }: { line: CartLine }) {
  if (line.reference?.price) {
    return (
      <>
        {formatEuro(line.reference.price)}
        <span className="block text-xs text-slate-500">{formatDateShort(line.reference.doc_date)}</span>
      </>
    );
  }
  return <span className="text-xs text-slate-500">{line.reference?.unit_unclear ? "Einheit nicht eindeutig" : "keine Referenz"}</span>;
}

/** Bester Rechnungspreis im Zeitraum des Warenkorbs (#630); der Großhändler nur, wenn es ein anderer ist. */
function BestPriceCell({ line, months }: { line: CartLine; months: number }) {
  const lowest = line.lowest_price;
  if (line.is_special) return <span className="text-slate-400">–</span>;
  if (!lowest) return <span className="text-slate-400" title={`Kein Rechnungspreis in den letzten ${bestPriceWindowLabel(months, "dative")}`}>–</span>;
  const other = lowest.supplier !== line.supplier;
  return (
    <span title={`Niedrigster Rechnungspreis der letzten ${bestPriceWindowLabel(months)}${lowest.doc_number ? ` · Beleg ${lowest.doc_number}` : ""}`}>
      {formatEuro(lowest.price)}
      <span className="block text-xs text-slate-500">{[other ? lowest.supplier : null, formatDateShort(lowest.doc_date)].filter(Boolean).join(" · ")}</span>
    </span>
  );
}

function RemoveLineButton({ line, edit, run }: { line: CartLine; edit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void }) {
  return (
    <button
      type="button"
      disabled={!edit}
      aria-label={`${line.article_code ?? "Position"} entfernen`}
      onClick={() => run((c) => removeCartLine(c, line.line_id))}
      className="rounded px-1 text-slate-400 hover:text-rose-700"
    >
      ×
    </button>
  );
}

/** One line on a phone: no sideways scrolling, the same values as the table. */
/** A special position has no article page to link to (#556). */
function LineTitle({ line }: { line: CartLine }) {
  const label = cleanDescription(line.description) || line.article_code || line.article_group_key || "Position";
  if (line.is_special || !line.article_group_key) {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">{label}</span>
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">Sonderposition</span>
      </span>
    );
  }
  return (
    <Link className="font-medium text-slate-900 hover:underline" to={`/articles/${encodeURIComponent(line.article_group_key)}`}>
      {label}
    </Link>
  );
}

function LineCard({ line, edit, run, cart }: { line: CartLine; edit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void; cart: Cart }) {
  return (
    <div className="border-t border-slate-100 px-4 py-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <LineTitle line={line} />
        {cart.can_edit ? <RemoveLineButton line={line} edit={edit} run={run} /> : null}
      </div>
      {line.is_special ? null : <p className="text-xs text-slate-500">{line.article_code}</p>}
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
        <div>
          <dt className="text-xs text-slate-500">Preis</dt>
          <dd className="mt-0.5">
            {line.is_special ? <AgreedPrice line={line} edit={edit} run={run} canEdit={cart.can_edit} /> : <ReferenceCell line={line} />}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Bestpreis</dt>
          <dd className="mt-0.5"><BestPriceCell line={line} months={cart.best_price_months} /></dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Menge</dt>
          <dd className="mt-0.5">{cart.can_edit ? <QtyField line={line} edit={edit} run={run} /> : `${formatQty(line.qty)} ${displayUnit(line.uom)}`}</dd>
        </div>
        <div className="text-right">
          <dt className="text-xs text-slate-500">Summe</dt>
          <dd className="mt-0.5 font-medium">{formatEuro(line.total)}</dd>
        </div>
        {hasHint(line, cart.can_edit) ? (
          <div className="col-span-2">
            <dt className="text-xs text-slate-500">Hinweis</dt>
            <dd className="mt-0.5"><Verdict line={line} edit={edit} run={run} canEdit={cart.can_edit} /></dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

/** One-off items: text, quantity, unit and the agreed price (#556). */
function SpecialLineForm({
  run,
  disabled,
  onDone,
}: {
  run: (fn: (cart: Cart) => Promise<Cart>) => void;
  disabled: boolean;
  onDone: () => void;
}) {
  const [description, setDescription] = useState("");
  const [qty, setQty] = useState("1");
  const [uom, setUom] = useState("Stk");
  const [price, setPrice] = useState("");
  const suppliers = useQuery({
    queryKey: ["reports", "price-report", "options"],
    queryFn: fetchPriceReportOptions,
    staleTime: 5 * 60_000,
  });
  const [supplier, setSupplier] = useState("");
  const parsedQty = parseAmount(qty);
  const parsedPrice = parseAmount(price);
  const ready = description.trim().length > 0 && !!parsedQty && parsedQty > 0 && !!parsedPrice && parsedPrice > 0;

  return (
    <form
      className="mt-3 grid gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 sm:grid-cols-[minmax(12rem,2fr)_6rem_6rem_8rem_auto]"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        run((cart) =>
          addSpecialCartLine(cart, {
            description: description.trim(),
            qty: parsedQty!,
            uom: uom.trim() || null,
            agreed_price: parsedPrice!,
            supplier: supplier || null,
          }),
        );
        setDescription("");
        setPrice("");
        onDone();
      }}
    >
      <label className="text-xs font-medium text-slate-700">
        Was wird gefertigt?
        <input
          value={description}
          maxLength={300}
          disabled={disabled}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="z. B. Duschabtrennung nach Maß laut Angebot"
          className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm"
        />
      </label>
      <label className="text-xs font-medium text-slate-700">
        Menge
        <input value={qty} inputMode="decimal" disabled={disabled} onChange={(event) => setQty(event.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-300 px-2 text-right text-sm" />
      </label>
      <label className="text-xs font-medium text-slate-700">
        Einheit
        <input value={uom} maxLength={20} disabled={disabled} onChange={(event) => setUom(event.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-300 px-2 text-sm" />
      </label>
      <label className="text-xs font-medium text-slate-700">
        Vereinbarter Preis
        <input value={price} inputMode="decimal" disabled={disabled} onChange={(event) => setPrice(event.target.value)} placeholder="0,00" className="mt-1 h-9 w-full rounded-md border border-slate-300 px-2 text-right text-sm" />
      </label>
      <label className="text-xs font-medium text-slate-700">
        Großhändler
        <Select containerClassName="mt-1 block" className="w-full" value={supplier} disabled={disabled} onChange={(event) => setSupplier(event.target.value)}>
          <option value="">offen</option>
          {(suppliers.data?.suppliers ?? []).map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </Select>
      </label>
      <p className="text-xs text-amber-900 sm:col-span-4">
        Einzelanfertigungen haben keine Preishistorie. Der vereinbarte Preis gilt; die Position fließt nicht in Artikel, Bestpreis oder Preisprüfung ein.
      </p>
      <div className="flex items-end gap-2">
        <button type="submit" className={PRIMARY} disabled={disabled || !ready}>Hinzufügen</button>
        <button type="button" className={BUTTON} onClick={onDone}>Abbrechen</button>
      </div>
    </form>
  );
}

function SupplierGroup(props: {
  cart: Cart;
  supplier: string | null;
  total: string;
  complete: boolean;
  lines: CartLine[];
  edit: boolean;
  run: (fn: (cart: Cart) => Promise<Cart>) => void;
}) {
  const { supplier, total, complete, lines } = props;
  // Vorgabe nur mit echter Unternehmensvorgabe; sonst wäre sie nur der Preis ein zweites Mal (#630).
  const showTarget = Boolean(props.cart.rule.revision) && lines.some((line) => line.target_price);
  // Hinweis compares wholesalers; with nothing to compare the column stays away.
  const showHint = lines.some((line) => hasHint(line, props.cart.can_edit));
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <h2 className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">
        <span>
          {supplier ?? "Ohne Großhändler"} · {lines.length} {lines.length === 1 ? "Position" : "Positionen"}
        </span>
        <span className="flex flex-wrap items-center gap-3 font-normal text-slate-600">
          <span>
            Zwischensumme <span className="font-semibold text-slate-900">{formatEuro(total)}</span>
            {complete ? null : " · unvollständig"}
          </span>
          <SupplierEnquiry cart={props.cart} supplier={supplier} lines={lines} />
        </span>
      </h2>
      <div className="md:hidden">
        {lines.map((line) => <LineCard key={line.line_id} line={line} {...props} />)}
      </div>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[880px] table-fixed text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Artikel</th>
              <th className="w-32 px-2 py-2 text-right font-medium">Preis</th>
              <th className="w-32 px-2 py-2 text-right font-medium">Bestpreis</th>
              {showHint ? <th className="w-56 px-2 py-2 font-medium">Hinweis</th> : null}
              {showTarget ? <th className="w-32 px-2 py-2 text-right font-medium">Vorgabe</th> : null}
              <th className="w-44 px-2 py-2 text-center font-medium">Menge</th>
              <th className="w-28 px-2 py-2 text-right font-medium">Summe</th>
              <th className="relative w-10 px-2 py-2"><span className="sr-only">Entfernen</span></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => <LineRow key={line.line_id} line={line} {...props} showTarget={showTarget} showHint={showHint} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Two steps: what will be created, then the order itself (#554). */
function ConfirmOrder({ cart, run, disabled }: { cart: Cart; run: (fn: (cart: Cart) => Promise<Cart>) => void; disabled: boolean }) {
  const [asking, setAsking] = useState(false);
  const navigate = useNavigate();
  const missingSupplier = cart.lines.some((line) => line.supplier === null);
  const blocked = !cart.cost_center || missingSupplier || cart.lines.length === 0;
  if (!asking) {
    return (
      <button
        type="button"
        className={PRIMARY}
        disabled={disabled || blocked}
        title={
          !cart.cost_center
            ? "Wähle zuerst die Kostenstelle"
            : missingSupplier
              ? "Jede Position braucht einen Großhändler"
              : undefined
        }
        onClick={() => setAsking(true)}
      >
        Bestellung bestätigen
      </button>
    );
  }
  return (
    <div role="group" aria-label="Bestellung prüfen" className="w-full rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
      <p className="font-medium text-emerald-900">
        {cart.groups.length === 1 ? "Es entsteht eine Bestellung:" : `Es entstehen ${cart.groups.length} Bestellungen:`}
      </p>
      <ul className="mt-1 space-y-0.5 text-emerald-900">
        {cart.groups.map((group) => (
          <li key={group.supplier ?? "none"}>
            {group.supplier} · {group.line_ids.length} {group.line_ids.length === 1 ? "Position" : "Positionen"} · {formatEuro(group.total)}
          </li>
        ))}
      </ul>
      <p className="mt-1 text-emerald-900">
        Kostenstelle {cart.cost_center?.label}. Jede Bestellung bekommt eine Bestellnummer, die du dem Großhändler mitgibst.
        TruuCost verschickt nichts.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          className={PRIMARY}
          disabled={disabled}
          onClick={() => {
            setAsking(false);
            run(async (current) => {
              const result = await confirmCart(current);
              // The cart is closed now; the new orders are what the buyer needs next.
              navigate("/bestellungen");
              return result.cart;
            });
          }}
        >
          Jetzt bestellen
        </button>
        <button type="button" className={BUTTON} onClick={() => setAsking(false)}>Abbrechen</button>
      </div>
    </div>
  );
}

/** Wholesaler of a line that was never bought anywhere (#554: an order needs one). */
function SupplierChoice({ line, edit, run }: { line: CartLine; edit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void }) {
  // The wholesalers this tenant actually buys from, same source as the price report.
  const suppliers = useQuery({
    queryKey: ["reports", "price-report", "options"],
    queryFn: fetchPriceReportOptions,
    staleTime: 5 * 60_000,
  });
  // Keep the choice in the field: a controlled reset to "" would fire a second,
  // empty change and send an invalid wholesaler.
  const [chosen, setChosen] = useState("");
  return (
    <Select
      size="sm"
      aria-label={`Großhändler ${line.article_code ?? ""}`}
      value={chosen}
      disabled={!edit || suppliers.isLoading}
      onChange={(event) => {
        const supplier = event.target.value;
        if (!supplier) return;
        setChosen(supplier);
        run((c) => updateCartLine(c, line.line_id, { supplier }));
      }}
    >
      <option value="">Großhändler wählen …</option>
      {(suppliers.data?.suppliers ?? []).map((item) => (
        <option key={item} value={item}>{item}</option>
      ))}
    </Select>
  );
}

/** Enquiry for one wholesaler: saves the list and opens the mail draft (#548). */
function SupplierEnquiry({ cart, supplier, lines }: { cart: Cart; supplier: string | null; lines: CartLine[] }) {
  const [sent, setSent] = useState(false);
  const { subject, body } = cartEnquiry(cart, supplier, lines);
  return (
    <span className="flex flex-wrap items-center gap-2">
      <a
        href={mailtoHref(subject, body)}
        className="inline-flex h-8 items-center rounded-md border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        onClick={() => {
          downloadOfferCsv(cartCsv(cart, lines), cartCsvFilename(cart, supplier));
          setSent(true);
        }}
      >
        Anfrage per E-Mail
      </a>
      {sent ? (
        <span role="status" className="text-xs text-slate-500">
          Liste gespeichert – an die E-Mail anhängen
        </span>
      ) : null}
    </span>
  );
}

function LineRow({ line, edit, run, cart, showTarget, showHint }: { line: CartLine; edit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void; cart: Cart; showTarget: boolean; showHint: boolean }) {
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="px-4 py-2">
        <LineTitle line={line} />
        {line.is_special ? null : <span className="block text-xs text-slate-500">{line.article_code}</span>}
      </td>
      <td className="px-2 py-2 text-right">
        {/* A special position has no reference: its price is the agreed one (#556). */}
        {line.is_special ? <AgreedPrice line={line} edit={edit} run={run} canEdit={cart.can_edit} /> : <ReferenceCell line={line} />}
      </td>
      <td className="px-2 py-2 text-right"><BestPriceCell line={line} months={cart.best_price_months} /></td>
      {showHint ? <td className="px-2 py-2"><Verdict line={line} edit={edit} run={run} canEdit={cart.can_edit} /></td> : null}
      {showTarget ? <td className="px-2 py-2 text-right">
        {line.target_price ? formatEuro(line.target_price) : <span className="text-slate-400">–</span>}
      </td> : null}
      <td className="px-2 py-2 text-center">
        {cart.can_edit ? <QtyField line={line} edit={edit} run={run} /> : `${formatQty(line.qty)} ${displayUnit(line.uom)}`}
      </td>
      <td className="px-2 py-2 text-right font-medium">{formatEuro(line.total)}</td>
      <td className="px-2 py-2 text-right">{cart.can_edit ? <RemoveLineButton line={line} edit={edit} run={run} /> : null}</td>
    </tr>
  );
}

/** Whether the Hinweis column has something to say for this line. */
function hasHint(line: CartLine, canEdit: boolean): boolean {
  // A special position already carries its label; "vereinbart" alone is no reason for the column.
  return (canEdit && line.supplier === null)
    || (line.has_reference && line.comparable_count >= 2)
    || (canEdit && Boolean(line.previous_supplier));
}

function Verdict({ line, edit, run, canEdit }: { line: CartLine; edit: boolean; canEdit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void }) {
  const badge = "rounded px-1.5 py-0.5 text-xs font-medium";
  return (
    <span className="flex flex-col items-start gap-1">
      {canEdit && line.supplier === null ? (
        <SupplierChoice line={line} edit={edit} run={run} />
      ) : line.is_special ? (
        <span className={`${badge} bg-slate-100 text-slate-600`} title="Einzelanfertigung: es gibt keinen Vergleichspreis, der vereinbarte Preis gilt.">
          vereinbart
        </span>
      ) : !line.has_reference || line.comparable_count < 2 ? (
        // Ohne Vergleich gibt es nichts zu sagen – kein Platzhalter-Badge (#630).
        <span className="text-slate-400" title={line.has_reference ? "In den letzten 12 Monaten nur bei einem Großhändler gekauft" : "Keine belastbare Vergleichsbasis"}>–</span>
      ) : line.cheaper_elsewhere && line.best ? (
        <>
          <span className={`${badge} bg-rose-50 text-rose-800`}>{line.best.supplier} günstiger · {formatEuro(line.best.price)}</span>
          {canEdit ? (
            <button type="button" disabled={!edit} className="text-xs text-slate-700 underline" onClick={() => run((c) => updateCartLine(c, line.line_id, { supplier: line.best!.supplier }))}>
              zu {line.best.supplier} verschieben
            </button>
          ) : null}
        </>
      ) : (
        <span className={`${badge} bg-emerald-50 text-emerald-800`}>guter Preis</span>
      )}
      {canEdit && line.previous_supplier ? (
        <button type="button" disabled={!edit} className="text-xs text-slate-500 underline" onClick={() => run((c) => updateCartLine(c, line.line_id, { undo_move: true }))}>
          zurück zu {line.previous_supplier}
        </button>
      ) : null}
    </span>
  );
}

const AGREED_FORMAT = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function AgreedPrice({ line, edit, run, canEdit }: { line: CartLine; edit: boolean; canEdit: boolean; run: (fn: (cart: Cart) => Promise<Cart>) => void }) {
  // Shown the way it is typed and read here: "1.240,00" rather than "1240.0000".
  const shown = line.agreed_price ? AGREED_FORMAT.format(Number(line.agreed_price)) : "";
  const [value, setValue] = useState(shown);
  useEffect(() => setValue(shown), [shown]);
  if (!canEdit) return line.agreed_price ? <>{formatEuro(line.agreed_price)}</> : <span className="text-slate-400">–</span>;
  const commit = () => {
    const parsed = parseAmount(value);
    if (!value.trim() && line.agreed_price) run((c) => updateCartLine(c, line.line_id, { clear_agreed_price: true }));
    else if (parsed && parsed > 0 && parsed !== toNumber(line.agreed_price)) run((c) => updateCartLine(c, line.line_id, { agreed_price: parsed }));
  };
  return (
    <input
      value={value}
      inputMode="decimal"
      disabled={!edit}
      placeholder="eingeben"
      title="Ohne belastbare Referenz gibt es keine Vorgabe. Ein vereinbarter Preis ist freiwillig."
      aria-label={`Vereinbarter Preis ${line.article_code ?? ""}`}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => event.key === "Enter" && commit()}
      className="h-8 w-24 rounded-md border border-slate-300 px-2 text-right text-sm"
    />
  );
}

function DeleteCart({ cart }: { cart: Cart }) {
  const queryClient = useQueryClient();
  const [, setParams] = useSearchParams();
  const remove = useMutation({
    mutationFn: () => deleteCart(cart),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: cartQueryKey(cart.cart_id) });
      void queryClient.invalidateQueries({ queryKey: ["carts", "list"] });
      setParams({});
    },
  });
  return (
    <button
      type="button"
      className="text-sm text-slate-500 underline-offset-2 hover:text-rose-700 hover:underline disabled:opacity-50"
      disabled={remove.isPending}
      onClick={() => window.confirm(`Warenkorb „${cart.name}“ löschen?`) && remove.mutate()}
    >
      Warenkorb löschen
    </button>
  );
}

function AllCarts({ onOpen }: { onOpen: (cartId: string) => void }) {
  const [scope, setScope] = useState<"mine" | "company">("mine");
  const [owner, setOwner] = useState("");
  const query = useQuery({ queryKey: ["carts", "list", scope, owner || null], queryFn: () => fetchCarts(scope, scope === "company" ? owner || null : null) });
  const everyone = useQuery({ queryKey: ["carts", "list", "company", null], queryFn: () => fetchCarts("company"), enabled: scope === "company" });
  const owners = useMemo(() => {
    const byId = new Map<string, string>();
    for (const cart of everyone.data?.items ?? []) byId.set(cart.owner.user_id, cart.owner.name);
    return [...byId].sort((a, b) => a[1].localeCompare(b[1], "de"));
  }, [everyone.data]);
  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {(["mine", "company"] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={scope === value}
            onClick={() => { setScope(value); setOwner(""); }}
            className={`h-8 rounded-full border px-3 text-sm ${scope === value ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-900" : "border-slate-300 bg-white text-slate-700"}`}
          >
            {value === "mine" ? "Meine" : "Firma"}
          </button>
        ))}
        {scope === "company" ? (
          <Select size="sm" aria-label="Person" value={owner} onChange={(event) => setOwner(event.target.value)}>
            <option value="">Alle Personen</option>
            {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </Select>
        ) : null}
      </div>
      {query.isLoading ? <p className="text-sm text-slate-500">Lade Warenkörbe …</p> : null}
      {query.isError ? <ErrorText error={query.error} fallback="Die Warenkörbe konnten nicht geladen werden." /> : null}
      {query.data ? <CartTable carts={query.data.items} onOpen={onOpen} /> : null}
    </div>
  );
}

export function CartTable({ carts, onOpen, emptyText = "Keine Warenkörbe." }: { carts: CartHeader[]; onOpen: (cartId: string) => void; emptyText?: string }) {
  if (!carts.length) return <p className="rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-600">{emptyText}</p>;
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] table-fixed text-sm">
        <thead className="text-left text-xs text-slate-500">
          <tr>
            <th className="px-4 py-2 font-medium">Warenkorb</th>
            <th className="px-2 py-2 font-medium">Person</th>
            <th className="px-2 py-2 font-medium">Kostenstelle</th>
            <th className="px-2 py-2 text-right font-medium">Positionen</th>
            <th className="px-2 py-2 font-medium">Status</th>
            <th className="px-2 py-2 font-medium">Geändert</th>
          </tr>
        </thead>
        <tbody>
          {carts.map((cart) => (
            <tr key={cart.cart_id} className="border-t border-slate-100">
              <td className="px-4 py-2 text-left">
                <button type="button" className="w-full text-left font-medium text-slate-900 hover:underline" onClick={() => onOpen(cart.cart_id)}>{cart.name}</button>
              </td>
              <td className="px-2 py-2">{cart.owner.is_me ? "ich" : cart.owner.name}</td>
              <td className="px-2 py-2">{cart.cost_center?.label ?? <span className="text-slate-400">Nicht zugeordnet</span>}</td>
              <td className="px-2 py-2 text-right">{cart.line_count}</td>
              <td className="px-2 py-2">{CART_STATUS_LABELS[cart.status]}</td>
              <td className="px-2 py-2">{formatDateShort(cart.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function ErrorText({ error, fallback }: { error: unknown; fallback: string }) {
  return (
    <p role="alert" className="mt-4 text-sm text-rose-700">
      {error instanceof ApiError ? error.message : fallback}
    </p>
  );
}
